import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import eventBus from '@/eventBus';

const GROUPS = [
  { field: 'assigneeIds', kind: 'users', labelKey: 'taskFilters.assignee' },
  { field: 'executorIds', kind: 'users', labelKey: 'taskFilters.executor' },
  { field: 'createdByIds', kind: 'users', labelKey: 'taskFilters.createdBy' },
  { field: 'tagIds', kind: 'tags', labelKey: 'taskFilters.tags' },
];

const FILTER_FIELDS = [
  'searchString', 'assigneeIds', 'executorIds', 'createdByIds', 'tagIds', 'createdAtRange',
];

const MAX_VISIBLE = 3;

function formatRange({ start, end }) {
  const from = dayjs(new Date(start));
  const to = dayjs(new Date(end));
  if (from.isSame(to, 'month')) {
    return `${from.format('D')} – ${to.format('D MMM YYYY')}`;
  }
  if (from.isSame(to, 'year')) {
    return `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
  }
  return `${from.format('D MMM YYYY')} – ${to.format('D MMM YYYY')}`;
}

export function useTaskFilterChips() {
  const store = useStore();
  const { t } = useI18n();

  const filter = computed(() => store.state.project.projectTasksFilter);
  const project = computed(() => store.state.project.project);
  const getUser = computed(() => store.getters['user/getUser']);

  function buildItems(field, kind, ids) {
    return ids.slice(0, MAX_VISIBLE).map((id) => {
      if (kind === 'tags') {
        const tag = id ? (project.value.labels || []).find(l => l.id === id) : null;
        return {
          key: `${field}-${id}`,
          id: tag ? tag.id : null,
          name: tag ? tag.name : t('search.noTag'),
          color: tag ? `#${tag.color}` : null,
        };
      }
      const usr = id ? getUser.value(id) : null;
      return {
        key: `${field}-${id}`,
        id: usr ? usr.id : null,
        name: usr ? usr.username : t('search.unassigned'),
        user: usr,
      };
    });
  }

  const chipGroups = computed(() => {
    const f = filter.value;
    const res = [];

    if (f.searchString) {
      res.push({
        key: 'searchString',
        kind: 'search',
        field: 'searchString',
        label: f.searchString,
        title: `${t('search.in_tasks')} ${f.searchString}`,
      });
    }

    GROUPS.forEach(({ field, kind, labelKey }) => {
      const ids = f[field] || [];
      if (!ids.length) {
        return;
      }
      res.push({
        key: field,
        kind,
        field,
        label: t(labelKey),
        items: buildItems(field, kind, ids),
        more: Math.max(0, ids.length - MAX_VISIBLE),
      });
    });

    if (f.createdAtRange) {
      res.push({
        key: 'createdAtRange',
        kind: 'date',
        field: 'createdAtRange',
        label: formatRange(f.createdAtRange),
        title: t('taskFilters.createdTime'),
      });
    }

    return res;
  });

  const activeCount = computed(() => {
    const f = filter.value;
    return (f.searchString ? 1 : 0)
      + (f.createdAtRange ? 1 : 0)
      + (f.tagIds || []).length
      + (f.assigneeIds || []).length
      + (f.executorIds || []).length
      + (f.createdByIds || []).length;
  });

  function clearGroup(group) {
    const next = { projectId: project.value.id };
    FILTER_FIELDS.forEach((field) => {
      if (field !== group.field) {
        next[field] = filter.value[field];
      }
    });
    store.dispatch('project/actionFilterTasks', next);
  }

  function clearItem(group, item) {
    store.dispatch('project/actionSwitchFilterByFieldAndId', { field: group.field, id: item.id });
  }

  function clearAll() {
    store.dispatch('project/actionFilterTasks', { projectId: project.value.id });
  }

  const openFilters = () => eventBus.$emit('toggle-tasks-filters');

  return {
    chipGroups,
    activeCount,
    clearGroup,
    clearItem,
    clearAll,
    openFilters,
  };
}
